import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Check } from 'lucide-react';
import { ShieldCheck, ArrowRight, CheckCircle, Star, CaretDown, Lightning, Users } from '@phosphor-icons/react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { toast } from 'sonner';
import LPHeader from '../components/LPHeader';
import LPFooter from '../components/LPFooter';
import TopInfoStrip from '../components/TopInfoStrip';
import InquiryModal from '../components/InquiryModal';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import PricingSectionV2 from '../components/PricingSectionV2';
import {
  selfFilingProcessCards,
  attorneyLedProcessCards,
  selfFilingPlans,
  attorneyLedPlans,
} from '../mock';

const countryCodes = [
  { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}', name: 'United States' },
  { code: '+1', country: 'CA', flag: '\u{1F1E8}\u{1F1E6}', name: 'Canada' },
  { code: '+44', country: 'GB', flag: '\u{1F1EC}\u{1F1E7}', name: 'United Kingdom' },
  { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}', name: 'India' },
  { code: '+92', country: 'PK', flag: '\u{1F1F5}\u{1F1F0}', name: 'Pakistan' },
  { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia' },
  { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}', name: 'Germany' },
  { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}', name: 'France' },
  { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}', name: 'Japan' },
  { code: '+86', country: 'CN', flag: '\u{1F1E8}\u{1F1F3}', name: 'China' },
  { code: '+55', country: 'BR', flag: '\u{1F1E7}\u{1F1F7}', name: 'Brazil' },
  { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}', name: 'UAE' },
  { code: '+966', country: 'SA', flag: '\u{1F1F8}\u{1F1E6}', name: 'Saudi Arabia' },
  { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}', name: 'Singapore' },
];

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');
  const [selectedPackage, setSelectedPackage] = useState({ title: 'Basic', amount: 45 });
  const [tmSignUpType, setTmSignUpType] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', description: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ email: '', phone: '' });

  useEffect(() => {
    (async () => {
      try {
        const info = await getCountryInfo();
        if (info) { const m = countryCodes.find(c => c.country === info.countryCode); if (m) setCountryCode(m.code); }
      } catch (e) { console.error('Geo error:', e); }
    })();
  }, []);

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const validatePhone = (p) => p.replace(/[^\d]/g, '').length === 10;

  const handleSubmit = async (e, source = 'LP Hero') => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Valid email required'; hasErrors = true; }
    if (!validatePhone(formData.phone)) { newErrors.phone = '10 digit phone required'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); toast.error('Please fix errors'); return; }

    setIsSubmitting(true);
    try {
      const leadData = { name: formData.name, email: formData.email, phone: formData.phone, countryCode, country: countryCodes.find(c => c.code === countryCode)?.country || 'US', description: formData.description || 'LP Lead' };
      const result = await submitLeadWithGeoLocation(leadData, null, source);
      if (result.success) { toast.success(result.message || 'Submitted!'); setFormData({ name: '', email: '', phone: '', description: '' }); }
      else { toast.error(result.message || 'Failed'); setIsSubmitting(false); }
    } catch (error) { console.error('Error:', error); toast.error('Please try again'); setIsSubmitting(false); }
  };

  const openModal = (payload) => {
    if (typeof payload === 'string') {
      setButtonContext(payload);
      setSelectedPackage({ title: 'Basic', amount: 45 });
      setTmSignUpType(null);
    } else {
      setButtonContext(payload.context || '');
      setSelectedPackage({
        title: payload.packageTitle || 'Basic',
        amount: payload.packageAmount || 45
      });
      setTmSignUpType(payload.tmSignUpType || null);
    }
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
       <TopInfoStrip />
      <LPHeader onOpenModal={openModal} />
   
      {/* 1. HEADER / HERO */}
      <section data-testid="lp-hero" className="relative min-h-[85vh] flex items-center overflow-hidden">
        <img src="https://images.unsplash.com/photo-1762008312967-beaf3f59984e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBkYXJrJTIwbW9kZXJuJTIwaW50ZXJpb3IlMjB3aWRlJTIwYW5nbGV8ZW58MHx8fHwxNzc1MDg4Njc5fDA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-16">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-5">
                Protect. Register. Grow. Dominate.
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white tracking-tighter leading-[1.05] mb-6">
                Trademark Registration Starting at $45
              </h1>
              <p className="text-base text-slate-300 leading-relaxed max-w-lg mb-8">
                Stop leaving your brand unprotected. Our licensed IP attorneys file your federal trademark application within 24-48 hours with a 98% USPTO approval rate. Secure exclusive nationwide rights to your name, logo, and slogan before someone else does.
              </p>
              <div className="space-y-3">
                {['Licensed IP attorneys with 98% USPTO approval rate', '24-48 hour expedited filing available', 'Comprehensive trademark search included'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0" weight="bold" />
                    <span className="text-sm text-slate-200 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="lp-hero-form" className="backdrop-blur-xl bg-white/95 border border-white/20 shadow-[0_8px_40px_rgba(0,0,0,0.3)] rounded-lg p-8">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl font-heading font-bold text-slate-900 mb-1">Register Your Trademark</h2>
                  <p className="text-sm text-[#044B3B] font-semibold">Just $45 + USPTO Fees</p>
                </div>
                <div className="w-10 h-10 bg-[#044B3B] rounded-md flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-white" weight="bold" />
                </div>
              </div>
              <form onSubmit={(e) => handleSubmit(e, 'LP Hero Form')} className="space-y-3">
                <Input data-testid="lp-name-input" type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="border-slate-200 focus:border-[#044B3B] rounded-md text-sm" />
                <div>
                  <Input data-testid="lp-email-input" type="email" placeholder="Email Address *" value={formData.email}
                    onChange={(e) => { setFormData({...formData, email: e.target.value}); if (errors.email && validateEmail(e.target.value)) setErrors(p => ({...p, email: ''})); }}
                    onBlur={() => { if (formData.email && !validateEmail(formData.email)) setErrors(p => ({...p, email: 'Valid email required'})); else setErrors(p => ({...p, email: ''})); }}
                    className={`${errors.email ? 'border-red-500' : 'border-slate-200'} focus:border-[#044B3B] rounded-md text-sm`} required />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <div className="flex gap-2">
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger data-testid="lp-country-select" className="w-24 px-2 py-2 border border-slate-200 rounded-md bg-white text-xs font-medium focus:border-[#044B3B]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {countryCodes.map((c, i) => (<SelectItem key={i} value={c.code}>{c.flag} {c.code}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Input data-testid="lp-phone-input" type="tel" placeholder="Phone Number *" value={formData.phone}
                      onChange={(e) => { const v = e.target.value.replace(/[^0-9\s\-]/g,''); const d = v.replace(/[^\d]/g,''); if(d.length>10)return; setFormData({...formData,phone:v}); if(errors.phone&&d.length===10) setErrors(p=>({...p,phone:''})); }}
                      onBlur={() => { const d=formData.phone.replace(/[^\d]/g,''); if(formData.phone&&d.length!==10) setErrors(p=>({...p,phone:'10 digit phone required'})); else setErrors(p=>({...p,phone:''})); }}
                      className={`flex-1 ${errors.phone?'border-red-500':'border-slate-200'} focus:border-[#044B3B] rounded-md text-sm`} required />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
                <Textarea data-testid="lp-description-input" placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} required className="border-slate-200 focus:border-[#044B3B] rounded-md text-sm resize-none" />
                <Button data-testid="lp-submit-btn" type="submit" disabled={isSubmitting} className="w-full bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-sm py-5 rounded-md">
                  {isSubmitting ? 'Submitting...' : <span className="flex items-center justify-center gap-2">Start My Registration <ArrowRight className="h-4 w-4" /></span>}
                </Button>
              </form>
              <p className="text-xs text-slate-400 text-center mt-3">Secure & confidential. No obligation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHY YOU NEED TRADEMARK */}
      <section className="py-20 bg-white" data-testid="lp-why-trademark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img src="https://images.pexels.com/photos/9858904/pexels-photo-9858904.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Legal trademark registration document with stamp" className="rounded-lg shadow-2xl w-full h-[420px] object-cover" />
              <div className="absolute -bottom-5 -right-5 bg-[#044B3B] text-white rounded-lg px-6 py-4 shadow-xl hidden lg:block">
                <p className="text-2xl font-heading font-bold">98%</p>
                <p className="text-xs text-white/70">Approval Rate</p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Why Register</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-6">Why Every Business Needs a Registered Trademark</h2>
              <p className="text-base text-slate-600 leading-relaxed">
                Without federal trademark registration, your brand name, logo, and slogan have zero legal protection outside your local area. Anyone in the country can use your exact brand identity, open competing businesses under your name, and sell products that confuse your customers. Trademark registration gives you the exclusive legal right to your brand across all 50 states, the power to sue infringers in federal court, eligibility for Amazon Brand Registry, and a legal presumption of ownership that deters copycats before they start. Companies with registered trademarks are valued 2-3x higher during acquisitions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. HOW WE WORK */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="lp-how-we-work">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Our Process</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">Three Steps to Federal Protection</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              We handle the legal complexity so you do not have to. From initial search to final registration, our attorneys manage every detail.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Free Consultation & Search', desc: 'Tell us about your brand. We conduct a comprehensive trademark search across USPTO databases, state registrations, and common law sources to identify conflicts before filing.' },
              { title: 'Attorney Review & Filing', desc: 'Your dedicated attorney reviews search results, selects the strongest filing strategy, prepares a complete application, and files with the USPTO within 24-48 hours.' },
              { title: 'Monitoring & Protection', desc: 'Track your application status in real-time. We respond to any office actions, handle examiner objections, and provide ongoing brand monitoring after registration.' }
            ].map((step, i) => (
              <div key={i} className="relative group">
                <div className="absolute -top-4 -left-2 text-[100px] font-heading font-bold text-slate-100 group-hover:text-white/10 leading-none select-none pointer-events-none transition-colors duration-300">{String(i+1).padStart(2,'0')}</div>
                <div className="relative bg-white border border-slate-200 hover:bg-[#044B3B] hover:border-[#044B3B] hover:shadow-xl transition-all duration-300 rounded-md p-8 pt-14">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 group-hover:text-white mb-3 transition-colors duration-300">{step.title}</h3>
                  <p className="text-sm text-slate-500 group-hover:text-white/80 leading-relaxed transition-colors duration-300">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. PRICING / PACKAGES */}
      <PricingSectionV2
        onOpenModal={openModal}
        selfFilingProcessCards={selfFilingProcessCards}
        attorneyLedProcessCards={attorneyLedProcessCards}
        selfFilingPlans={selfFilingPlans}
        attorneyLedPlans={attorneyLedPlans}
      />

      {/* 5. WHY CHOOSE US */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="lp-why-choose">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Why Choose Us</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-medium text-slate-900 tracking-tight mb-4">The Trusted Choice for IP Protection</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                We are not a DIY filing platform. We are a team of licensed IP attorneys who file, fight, and win for your brand.
              </p>
              <div className="space-y-6">
                {[
                  { icon: ShieldCheck, title: '98% USPTO Approval Rate', desc: 'Our meticulous pre-filing analysis and comprehensive search process virtually eliminates rejection risk. If your trademark is not approved, you get a full refund.' },
                  { icon: Lightning, title: '24-48 Hour Filing Speed', desc: 'While other firms take weeks, we file your trademark application within 24-48 hours. Faster filing means earlier priority dates and faster protection.' },
                  { icon: Users, title: 'Dedicated Attorney on Every Case', desc: 'Every application is handled by a USPTO-registered trademark attorney — not a paralegal, not an AI tool. A real attorney who fights for your registration.' }
                ].map((usp, i) => {
                  const Icon = usp.icon;
                  return (
                    <div key={i} className="flex gap-4 p-4 rounded-lg hover:bg-[#044B3B] transition-colors duration-300 group">
                      <div className="w-11 h-11 bg-[#044B3B] rounded-md flex items-center justify-center flex-shrink-0 group-hover:bg-white/20"><Icon className="h-5 w-5 text-white" weight="regular" /></div>
                      <div>
                        <h3 className="text-base font-heading font-semibold text-slate-900 mb-1 group-hover:text-white">{usp.title}</h3>
                        <p className="text-sm text-slate-500 leading-relaxed group-hover:text-white/90">{usp.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <img src="https://images.pexels.com/photos/7876154/pexels-photo-7876154.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Attorney consulting client about IP protection" className="rounded-lg shadow-2xl w-full h-[480px] object-cover" />
              <div className="absolute -bottom-5 -left-5 bg-[#044B3B] text-white rounded-lg px-6 py-4 shadow-xl hidden lg:block">
                <p className="text-2xl font-heading font-bold">10,000+</p>
                <p className="text-xs text-white/70">Brands Protected</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. CTA SECTION */}
      <section className="py-20 relative overflow-hidden" data-testid="lp-cta">
        <img src="/assets/img/cta-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#044B3B]/90" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-white tracking-tight mb-6">Someone Could File Your Brand Name Tomorrow</h2>
          <p className="text-base text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            Trademark law is first-come, first-served. Every day you wait is another day a competitor can file your exact brand name. The average forced rebrand costs $200,000+. Registration starts at $45. Do the math.
          </p>
          <Button data-testid="lp-cta-register" onClick={() => openModal('LP Bottom CTA')} className="bg-white text-[#044B3B] hover:bg-slate-100 font-semibold text-sm px-10 py-5 rounded-md">
            <span className="flex items-center gap-2">Lock In My Brand Name Now <ArrowRight className="h-4 w-4" /></span>
          </Button>
        </div>
      </section>

      {/* 7. TESTIMONIALS */}
      <TestimonialsSlider />

      {/* 8. FAQs */}
      <FAQSection />

      <LPFooter />
      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={buttonContext} packageTitle={selectedPackage.title} packageAmount={selectedPackage.amount} tmSignUpType={tmSignUpType} />
    </div>
  );
};

const TestimonialsSlider = () => {
  const [emblaRef] = useEmblaCarousel({ loop: true, align: 'start', duration: 45 }, [Autoplay({ delay: 3500, stopOnInteraction: false })]);
  const testimonials = [
    { name: 'Sarah M.', role: 'eCommerce Founder', content: 'They registered my brand trademark in record time. The comprehensive search gave me confidence that my brand name was clear, and the filing was done within 48 hours. Now I am fully enrolled in Amazon Brand Registry.' },
    { name: 'Alex R.', role: 'Software Developer', content: 'Registered my software source code and UI designs through their copyright service. When a competitor copied our interface, the federal registration was the key evidence in our infringement lawsuit. We won.' },
    { name: 'Dr. Alan K.', role: 'Patent Inventor', content: 'Their patent search found a closely related patent we had no idea about. We modified our claims and filed a much stronger application. That one search saved us tens of thousands in wasted filing fees.' },
    { name: 'Jake T.', role: 'LLC Founder', content: 'Formed my LLC in Delaware quickly and professionally. The custom operating agreement was thorough and covered everything — ownership, management, distributions. Had my EIN within days.' },
    { name: 'Priya S.', role: 'Fashion Designer', content: 'A competitor tried to copy my brand name. Thanks to my registered trademark, we shut them down with a single cease-and-desist letter. Best $45 I ever spent on my business.' },
  ];

  return (
    <section className="py-20 bg-white" data-testid="lp-testimonials">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">Real Results</p>
          <h2 className="text-3xl font-heading font-medium text-slate-900 tracking-tight">What Our Clients Say About Us</h2>
        </div>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="flex-[0_0_100%] md:flex-[0_0_calc(50%-10px)] lg:flex-[0_0_calc(33.333%-14px)] min-w-0">
                <Card className="p-7 h-full border border-slate-200 rounded-md">
                  <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, j) => (<Star key={j} className="h-4 w-4 text-[#D4AF37]" weight="fill" />))}</div>
                  <p className="text-sm text-slate-600 mb-5 leading-relaxed italic">&ldquo;{t.content}&rdquo;</p>
                  <div className="pt-4 border-t border-slate-100 mt-auto">
                    <h4 className="text-sm font-semibold text-slate-900">{t.name}</h4>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: 'How long does trademark registration take and what does the $45 package include?', a: 'The USPTO typically takes 8-12 months for full registration, but we file your application within 24-48 hours so your brand gets "pending" protection immediately. The $45 Essentials package includes a case review, basic trademark search, USPTO filing, trademark secured status, 1 month of infringement alerts, and a cease-and-desist letter template.' },
    { q: 'What is the difference between a trademark, copyright, and patent?', a: 'A trademark protects brand identifiers (names, logos, slogans) used in commerce. Copyright protects original creative works (software, music, books, art, photography). Patents protect inventions and unique processes. Each serves a different purpose, and most businesses need at least two of the three.' },
    { q: 'Do I need an LLC or corporation to register a trademark?', a: 'No. Individuals, sole proprietors, partnerships, LLCs, and corporations can all register trademarks. However, forming an LLC or corporation provides liability protection for your business. We offer both trademark registration and business formation services.' },
    { q: 'What happens if my trademark or patent application is rejected?', a: 'If your application receives an office action (objection), our attorneys review the rejection, identify the issue, and prepare a comprehensive legal response. Our 98% approval rate reflects our expertise in overturning objections. For patents, we modify claims to distinguish your invention from prior art.' },
    { q: 'Can I protect my brand, creative work, and inventions internationally?', a: 'Yes. We assist with international trademark registration through the Madrid Protocol, international copyright enforcement through bilateral treaties, and international patent protection through PCT applications. Our team advises on the best global strategy for your specific IP portfolio.' },
  ];

  return (
    <section className="py-20 bg-[#FAFAFA]" data-testid="lp-faq">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#044B3B] mb-3">FAQ</p>
          <h2 className="text-3xl font-heading font-medium text-slate-900 tracking-tight">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-${i}`}>
                <span className="text-sm font-semibold text-slate-900 pr-4">{faq.q}</span>
                <CaretDown className={`h-4 w-4 text-slate-500 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5 -mt-1">
                  <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LandingPage;
